import { useContext, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AppContext from '../contexts/AppContext';
import DataContext from '../contexts/DataContext';
import Input from './Input';
import Button from './Button';
import PhoneNumberInput from './PhoneNumberInput';
import { useNavigate } from 'react-router-dom';
import ConfigContext from '../contexts/ConfigContext';
import ConsentWidget, {
  type ConsentForm,
  GLOBAL_BACKEND_API,
} from './ConsentWidget';
import { generateFunnelAuthJwt } from '../utils/funnelAuthJwt';

export default function ContactInformation({
  scrollToRef,
}: {
  scrollToRef: any;
}) {
  const { isDebrandedVersion, features } = useContext(ConfigContext);
  const {
    slides,
    currentSlide,
    updateSelectedOption,
    urlParams,
    showClickToCall,
  } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const { i18n } = useTranslation();
  const localeCode = i18n.language || 'en';
  const navigate = useNavigate();
  const [nameHaveError, setNameHaveError] = useState(false);
  const [phoneInputValue, setPhoneInputValue] = useState(
    slides[currentSlide].additionalFields.phone || ''
  );
  const consentFormRef = useRef<ConsentForm | null>(null);
  const [consentAllChecked, setConsentAllChecked] = useState(false);

  useEffect(() => {
    setTimeout(
      scrollToRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      })
    );
  }, [scrollToRef]);

  const handleNameInput = (e: any) => {
    const name = e.target.value.trim();
    const nameArray = name.split(' ');

    if (nameArray.length >= 2) {
      updateSelectedOption(slides[currentSlide].key, name);
      updateData({
        ...data,
        fullName: name,
      });
      setNameHaveError(false);
    } else {
      updateSelectedOption(slides[currentSlide].key, null);
      updateData({
        ...data,
        fullName: null,
      });
      setNameHaveError(true);
    }
  };

  const handlePhoneInput = (newValue: any, haveError: boolean) => {
    setPhoneInputValue(newValue);
    const newData = haveError ? null : newValue;
    updateSelectedOption(slides[currentSlide].key, newData, 'phone');
    updateData({
      ...data,
      phone: newData,
    });
  };

  const submitForm = async () => {
    dataLayer.push({ event: 'seform_contact_information_entered' });

    if (process.env.REACT_APP_ENV !== 'local') {
      const lastSubmittedTime = localStorage.getItem('slf-last-submitted');
      if (lastSubmittedTime) {
        const now = Date.now();
        const secondsPassed = (now - Number(lastSubmittedTime)) / 1000;
        if (secondsPassed < 60 * 5) return;
      }
      localStorage.setItem('slf-last-submitted', String(Date.now()));
    }

    if (consentFormRef.current) {
      try {
        const consentPayload =
          await consentFormRef.current.getSubmissionPayload();
        updateData({ ...data, consentPayload });
        const token = await generateFunnelAuthJwt();
        if (!token) {
          console.warn('ConsentWidget: failed to generate JWT token');
          return;
        }
        const consentUrl = `${GLOBAL_BACKEND_API}/v1/consent/update?locale=${localeCode}`;
        const response = await fetch(consentUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...consentPayload,
            email: data.email,
            phoneNumber: data.phone,
          }),
        });
        if (!response.ok) {
          const errorText = await response.text();
        }
      } catch (e) {
        console.warn('ConsentWidget: failed to submit consent payload', e);
      }
    }

    navigate(`/calculating${urlParams}`);
  };

  return (
    <div>
      <p className="mt-[30px] text-[17px] short:!text-[16px] md:text-[18px]">
        Full name
      </p>
      <Input
        name="full_name"
        placeholder="First and last name"
        onChange={(e: any) => handleNameInput(e)}
        className="mt-3 md:mt-4 mb-6 md:mb-6 md:w-[400px]"
        errorMessage="Please enter first and last name"
        haveError={nameHaveError}
        defaultValue={slides[currentSlide].selectedValue}
      />

      <PhoneNumberInput
        label="Phone"
        value={phoneInputValue}
        onChange={handlePhoneInput}
      />

      <input type="hidden" id="leadid_tcpa_disclosure" />
      <ConsentWidget
        country="US"
        onReady={(form) => {
          consentFormRef.current = form;
          setConsentAllChecked(true);
        }}
      />

      <div className="mt-[18px] md:mt-[40px]">
        <Button
          title="See report and get estimates"
          action={submitForm}
          disabled={
            !slides[currentSlide].selectedValue ||
            !slides[currentSlide].additionalFields.phone ||
            !consentAllChecked
          }
        />
      </div>
    </div>
  );
}
